/**
 * Google Analytics Service
 * 
 * Handles all analytics tracking for the application including:
 * - Guest user behavior
 * - Conversion tracking
 * - Feature usage
 * - QR code flows
 */

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

interface AnalyticsConfig {
  measurementId: string;
  debug?: boolean;
}

interface UserProperties {
  user_type: 'guest' | 'registered' | 'anonymous';
  guest_id?: string;
  user_id?: string;
  session_start?: string;
  session_expires?: string;
  entry_source?: 'direct' | 'qr_code' | 'referral' | 'organic';
}

interface EventParams {
  [key: string]: any;
}

class AnalyticsService {
  private initialized = false;
  private config: AnalyticsConfig | null = null;
  private userProperties: UserProperties = { user_type: 'anonymous' };

  /**
   * Initialize Google Analytics with configuration from backend
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      console.log('[Analytics] Already initialized');
      return;
    }

    try {
      // Fetch config from backend
      const config = await this.fetchAnalyticsConfig();
      if (!config || !config.measurementId) {
        console.warn('[Analytics] No measurement ID configured');
        return;
      }

      this.config = config;

      // Load gtag script
      await this.loadGtagScript(config.measurementId);

      // Initialize gtag
      window.gtag('js', new Date());
      window.gtag('config', config.measurementId, {
        send_page_view: false, // We'll handle page views manually
        debug_mode: config.debug || false
      });

      this.initialized = true;
      console.log('[Analytics] Initialized with measurement ID:', config.measurementId);
    } catch (error) {
      console.error('[Analytics] Failed to initialize:', error);
    }
  }

  /**
   * Fetch analytics configuration from backend
   */
  private async fetchAnalyticsConfig(): Promise<AnalyticsConfig | null> {
    try {
      // Use the correct app config endpoint with domain-based app name
      const appName = (await import('@/utils/appConfig')).getAppNameForDomain();
      const response = await fetch(`/api/v1/config/app/${appName}`);
      if (!response.ok) {
        // Use fallback for non-200 responses
        console.debug('[Analytics] Config endpoint not available, using fallback');
        return {
          measurementId: 'G-8KMK9FHY60',
          debug: false
        };
      }
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Non-JSON response means endpoint doesn't exist or returns HTML
        console.debug('[Analytics] Config endpoint returned non-JSON, using fallback');
        return {
          measurementId: 'G-8KMK9FHY60',
          debug: false
        };
      }
      
      const data = await response.json();
      
      // Check for analytics configuration in the app config
      // The analytics config might be in integrations.google or features.enableAnalytics
      if (data.integrations?.google?.analytics?.measurementId) {
        return {
          measurementId: data.integrations.google.analytics.measurementId,
          debug: false
        };
      }
      
      // Fallback to known measurement ID if not in config
      return {
        measurementId: 'G-8KMK9FHY60',
        debug: false
      };
    } catch (error) {
      console.debug('[Analytics] Config not available:', error);
      // Use fallback configuration
      return {
        measurementId: 'G-8KMK9FHY60',
        debug: false
      };
    }
  }

  /**
   * Load Google Analytics gtag script
   */
  private loadGtagScript(measurementId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.gtag) {
        resolve();
        return;
      }

      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      // Load script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load gtag script'));
      document.head.appendChild(script);
    });
  }

  /**
   * Set user properties for all future events
   */
  setUserProperties(properties: Partial<UserProperties>): void {
    if (!this.initialized) {
      console.warn('[Analytics] Not initialized, queueing user properties');
      this.userProperties = { ...this.userProperties, ...properties };
      return;
    }

    this.userProperties = { ...this.userProperties, ...properties };
    
    window.gtag('set', 'user_properties', this.userProperties);
    console.log('[Analytics] User properties set:', this.userProperties);
  }

  /**
   * Track a custom event
   */
  trackEvent(eventName: string, params?: EventParams): void {
    if (!this.initialized) {
      console.warn('[Analytics] Not initialized, event not tracked:', eventName);
      return;
    }

    // Safety check for gtag
    if (typeof window === 'undefined' || !window.gtag) {
      console.warn('[Analytics] gtag not available, event not tracked:', eventName);
      return;
    }

    try {
      window.gtag('event', eventName, {
        ...params,
        user_type: this.userProperties.user_type,
        guest_id: this.userProperties.guest_id,
        timestamp: new Date().toISOString()
      });

      console.log('[Analytics] Event tracked:', eventName, params);
    } catch (error) {
      console.error('[Analytics] Error tracking event:', error);
    }
  }

  /**
   * Track page view
   */
  trackPageView(path: string, title?: string): void {
    if (!this.initialized || typeof window === 'undefined' || !window.gtag) {
      return;
    }

    try {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title,
        user_type: this.userProperties.user_type
      });
    } catch (error) {
      console.error('[Analytics] Error tracking page view:', error);
    }
  }

  // Guest User Events
  
  /**
   * Track guest login
   */
  trackGuestLogin(guestId: string, sessionExpiry: string): void {
    this.setUserProperties({
      user_type: 'guest',
      guest_id: guestId,
      session_start: new Date().toISOString(),
      session_expires: sessionExpiry
    });

    this.trackEvent('guest_login', {
      method: 'guest',
      guest_id: guestId,
      session_duration_hours: 48
    });
  }

  /**
   * Track guest feature usage
   */
  trackGuestFeatureUse(feature: 'pay' | 'redeem', details?: EventParams): void {
    this.trackEvent('guest_feature_use', {
      feature_name: feature,
      ...details
    });
  }

  /**
   * Track when guest hits restriction
   */
  trackGuestRestriction(attemptedPath: string): void {
    this.trackEvent('guest_restriction_hit', {
      attempted_path: attemptedPath,
      allowed_paths: ['/pay', '/redeem']
    });
  }

  /**
   * Track upgrade prompt shown
   */
  trackUpgradePromptShown(location: string, variant: string): void {
    this.trackEvent('guest_upgrade_prompt_shown', {
      prompt_location: location,
      prompt_variant: variant
    });
  }

  /**
   * Track upgrade clicked
   */
  trackUpgradeClicked(location: string): void {
    this.trackEvent('guest_upgrade_clicked', {
      click_location: location
    });
  }

  /**
   * Track successful conversion from guest to registered
   */
  trackGuestConversion(guestId: string, userId: string, timeToConvert: number): void {
    this.trackEvent('guest_to_registered', {
      guest_id: guestId,
      user_id: userId,
      time_to_convert_minutes: Math.round(timeToConvert / 60000),
      conversion_value: 1
    });

    // Update user properties
    this.setUserProperties({
      user_type: 'registered',
      user_id: userId,
      guest_id: undefined
    });
  }

  /**
   * Track guest session expiry
   */
  trackGuestSessionExpired(guestId: string): void {
    this.trackEvent('guest_session_expired', {
      guest_id: guestId,
      session_duration_hours: 48
    });
  }

  // QR Code Flow Events

  /**
   * Track QR code scan landing
   */
  trackQRCodeLanding(type: 'payment' | 'redemption', code?: string): void {
    this.setUserProperties({
      entry_source: 'qr_code'
    });

    this.trackEvent('qr_scan_landing', {
      qr_type: type,
      qr_code: code,
      has_auth: false
    });
  }

  /**
   * Track QR guest selection
   */
  trackQRGuestSelected(type: 'payment' | 'redemption'): void {
    this.trackEvent('qr_guest_selected', {
      qr_type: type,
      auth_choice: 'guest'
    });
  }

  /**
   * Track QR login selection
   */
  trackQRLoginSelected(type: 'payment' | 'redemption'): void {
    this.trackEvent('qr_login_selected', {
      qr_type: type,
      auth_choice: 'login'
    });
  }

  /**
   * Track QR action completed
   */
  trackQRActionCompleted(type: 'payment' | 'redemption', value?: number, currency?: string): void {
    this.trackEvent('qr_action_completed', {
      qr_type: type,
      transaction_value: value,
      transaction_currency: currency,
      success: true
    });
  }

  /**
   * Track QR abandonment
   */
  trackQRAbandonment(type: 'payment' | 'redemption', stage: string): void {
    this.trackEvent('qr_abandonment', {
      qr_type: type,
      abandonment_stage: stage
    });
  }

  // E-commerce Events (for conversion tracking)

  /**
   * Track begin checkout (upgrade flow)
   */
  trackBeginCheckout(currency: string = 'USD'): void {
    this.trackEvent('begin_checkout', {
      currency,
      value: 0,
      items: [{
        item_id: 'account_upgrade',
        item_name: 'Account Upgrade from Guest',
        item_category: 'conversion',
        price: 0,
        quantity: 1
      }]
    });
  }

  /**
   * Track purchase (successful registration)
   */
  trackPurchase(userId: string, currency: string = 'USD'): void {
    this.trackEvent('purchase', {
      transaction_id: `upgrade_${userId}_${Date.now()}`,
      currency,
      value: 0,
      items: [{
        item_id: 'account_upgrade',
        item_name: 'Account Upgrade from Guest',
        item_category: 'conversion',
        price: 0,
        quantity: 1
      }]
    });
  }

  // Utility Methods

  /**
   * Get current user properties
   */
  getUserProperties(): UserProperties {
    return { ...this.userProperties };
  }

  /**
   * Check if analytics is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Reset analytics (useful for logout)
   */
  reset(): void {
    this.userProperties = { user_type: 'anonymous' };
    if (this.initialized) {
      window.gtag('set', 'user_properties', this.userProperties);
    }
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

// Export types
export type { UserProperties, EventParams };
